import { Result } from "@bridgesplit/utils";

import {
    ApiLoanEventActionMetadata,
    ApiParsedLoanEvent,
    ParsedBorrowPrincipalMetadataApi,
    ParsedRepayPrincipalMetadataApi,
    ParsedRefinanceLedgerMetadataApi,
    ParsedSellLedgerMetadataApi,
    ParsedLiquidateLedgerMetadataApi,
    ParsedEventLedgerApi
} from "../api/events";
import {
    ParsedLoanEventActionMetadata,
    LoanEventActionType,
    ParsedRepayPrincipalMetadata,
    ParsedRefinanceLedgerMetadata,
    ParsedSellLedgerMetadata,
    ParsedLiquidateLedgerMetadata,
    ParsedEventLedger,
    LiquidateEventType,
    ParsedLoanEvent,
    ParsedBorrowPrincipalMetadata
} from "../domain/events";

function parseEventLedgerFromApi(api: ParsedEventLedgerApi): ParsedEventLedger {
    return {
        ledgerIndex: api.ledgerIndex,
        status: api.status,
        strategy: api.strategy,
        principalMint: api.principalMint,
        marketInformation: api.marketInformation,
        principalDue: api.principalDue,
        principalRepaid: api.principalRepaid,
        interestOutstanding: api.interestOutstanding,
        duration: api.duration,
        interestPerSecond: api.interestPerSecond,
        apy: api.apy,
        endTime: api.endTime
    };
}

function parseBorrowPrincipalMetadataFromApi(api: ParsedBorrowPrincipalMetadataApi): ParsedBorrowPrincipalMetadata {
    return {
        preBorrowPrincipalLedgerState: parseEventLedgerFromApi(api.preBorrowPrincipalLedgerState),
        postBorrowPrincipalLedgerState: parseEventLedgerFromApi(api.postBorrowPrincipalLedgerState)
    };
}

function parseRepayPrincipalMetadataFromApi(api: ParsedRepayPrincipalMetadataApi): ParsedRepayPrincipalMetadata {
    return {
        preRepaymentPrincipalLedgerState: parseEventLedgerFromApi(api.preRepaymentPrincipalLedgerState),
        postRepaymentPrincipalLedgerState: parseEventLedgerFromApi(api.postRepaymentPrincipalLedgerState)
    };
}

function parseRefinanceLedgerMetadataFromApi(api: ParsedRefinanceLedgerMetadataApi): ParsedRefinanceLedgerMetadata {
    return {
        preRefinanceLedgerState: parseEventLedgerFromApi(api.preRefinanceLedgerState),
        postRefinanceLedgerState: parseEventLedgerFromApi(api.postRefinanceLedgerState)
    };
}

function parseSellLedgerMetadataFromApi(api: ParsedSellLedgerMetadataApi): ParsedSellLedgerMetadata {
    return {
        buyer: api.buyer,
        seller: api.seller
    };
}

function parseLiquidateLedgerMetadataFromApi(api: ParsedLiquidateLedgerMetadataApi): ParsedLiquidateLedgerMetadata {
    return {
        ledgerIndex: api.ledgerIndex,
        isTimeBasedLiquidation: api.isTimeBasedLiquidation,
        liquidationFee: api.liquidationFee,
        collateralTransfers: api.collateralTransfers.map((transfer) => ({
            amount: transfer.amount,
            assetMint: transfer.assetMint,
            toLiquidator: transfer.toLiquidator,
            price: transfer.price,
            liquidateEventType: transfer.toLiquidator
                ? LiquidateEventType.LiquidatedCollateral
                : LiquidateEventType.CollateralReturned
        }))
    };
}

function parseLoanEventActionMetadataFromApi(api: ApiLoanEventActionMetadata): ParsedLoanEventActionMetadata {
    const runtimeType = api.type;

    switch (api.type) {
        case "addCollateral":
            return { type: LoanEventActionType.AddCollateral, params: null };
        case "removeCollateral":
            return { type: LoanEventActionType.RemoveCollateral, params: null };
        case "borrowPrincipal":
            return {
                type: LoanEventActionType.BorrowPrincipal,
                params: parseBorrowPrincipalMetadataFromApi(api.params)
            };
        case "repayPrincipal":
            return {
                type: LoanEventActionType.RepayPrincipal,
                params: parseRepayPrincipalMetadataFromApi(api.params)
            };
        case "refinanceLedger":
            return {
                type: LoanEventActionType.RefinanceLedger,
                params: parseRefinanceLedgerMetadataFromApi(api.params)
            };
        case "sellLedger":
            return {
                type: LoanEventActionType.SellLedger,
                params: parseSellLedgerMetadataFromApi(api.params)
            };
        case "liquidate":
            return {
                type: LoanEventActionType.Liquidate,
                params: parseLiquidateLedgerMetadataFromApi(api.params)
            };
        case "migrate":
            throw new Error(`Unsupported ApiLoanEventActionMetadata type: ${api.type}`);
        default:
            throw new Error(`Unknown ApiLoanEventActionMetadata type: ${runtimeType}`);
    }
}

export function parseLoanEventFromApi(api: ApiParsedLoanEvent): ParsedLoanEvent {
    return {
        ...api,
        actionMetadata: parseLoanEventActionMetadataFromApi(api.actionMetadata)
    };
}

export function tryParseLoanEventFromApi(api: ApiParsedLoanEvent): ParsedLoanEvent | null {
    try {
        return parseLoanEventFromApi(api);
    } catch (error) {
        const context = {
            loanEventId: api.id,
            action: api.action,
            actionMetadataType: api.actionMetadata.type,
            error
        };

        Result.errFromMessage<never>("Failed to parse loan event; skipping event", {
            rawError: context,
            skipSentry: true
        });
        return null;
    }
}
